import guid from '@sparta.global.utilities/guid-utility/dist/es6.js';

/**
 * @name        Sentinel Cookie Utility
 * @file        sentinel-cookie-utility.js
 * @description Create sentinel (BOA_0020) cookie.
 * @classdesc   Create sentinel (BOA_0020) cookie.
 * @global      Creates a global SentinelCookieUtility object
 * @author      Sunny Singh
 * @copyright   © 2018 Bank of America Corporation. All rights reserved.
 */

// Cookie settings.
const COOKIE_NAME = 'BOA_0020';
const COOKIE_EXPIRATION = 7300; // days
const COOKIE_PATH = '/';
const COOKIE_DOMAIN = '.bankofamerica.com';
const COOKIE_PATTERN = /^(\d{8}):0:O:([0-9a-f]{8})-(([0-9a-f]{4}-){2})([0-9a-f]{16})$/im;

/**
 * @description Get date (yyyymmdd format).
 * @method getDate
 * @returns {String} Date.
 */
function getDate() {
  const date = new Date();
  const mm = date.getMonth() + 1; // getMonth() is zero-based
  const dd = date.getDate();
  const year = date.getFullYear();
  const month = (mm > 9 ? '' : '0') + mm;
  const day = (dd > 9 ? '' : '0') + dd;
  return year + month + day;
}

/**
 * @description Get GUID with last dash removed (xxxxxxxx-xxxx-4xxx-yxxxxxxxxxxxxxxx format).
 * @method generateSentinalId
 * @returns {String} Sentinal ID.
 */
function generateSentinalId() {
  const id = guid();
  const index = id.lastIndexOf('-');
  return id.slice(0, index) + id.slice(index + 1);
}

/**
 * @description Check if cookie already exists.
 * @cookieExists
 * @returns {Boolean} Cookie exists.
 */
function cookieExists() {
  const cookies = window.document.cookie.split(/; /g).filter(Boolean);
  return cookies.some(cookie => cookie.split('=')[0] === COOKIE_NAME);
}

/**
 * @description Get cookie.
 * @method getCookie
 * @returns {String} The cookie value
 */
function getCookie() {
  const lookup = `${COOKIE_NAME}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArr = decodedCookie.split(';');
  for (let i = 0; i < cookieArr.length; i++) {
    let cookie = cookieArr[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(lookup) === 0) {
      const returnVal = cookie.substring(lookup.length, cookie.length);
      return returnVal;
    }
  }
  return '';
}

/**
 * @description Set cookie.
 * @method setCookie
 * @param {String} value Cookie value.
 * @returns {void}
 */
function setCookie(value) {
  const isLocal = spaParams.siteParams.env === 'local';
  const cookieValue = value.replace(new RegExp('[^-a-zA-Z0-9:_ ]'), '');
  const expiry = `; expires=${new Date(Date.now() + COOKIE_EXPIRATION * 24 * 60 * 60 * 1000).toUTCString()}`;
  const domain = isLocal ? '' : `; domain=${COOKIE_DOMAIN}`;
  const secure = !isLocal ? '; secure' : '';
  const cookie = `${COOKIE_NAME}=${cookieValue}${expiry}; path=${COOKIE_PATH}${domain}${secure}`;
  window.document.cookie = cookie;
}

/**
 * @description Checks if the cookie value is valid.
 * @method isValid
 * @returns {Boolean} False if cookie doesn't exists and true or false matching test pattern.
 */
function isValid() {
  if (!cookieExists()) return false;
  return COOKIE_PATTERN.test(getCookie());
}

/**
 * @description Sets cookie if it doesn't exist.
 * @method init
 * @returns {Boolean} True if created cookie, false if cookie already exists in valid format.
 */
function init() {
  if (isValid()) return false;
  setCookie(`${getDate()}:0:O:${generateSentinalId()}`);
  return true;
}

export { cookieExists, generateSentinalId, getCookie, getDate, init, isValid, setCookie };
